/**
 * 맥스매치 Full·Lite 공통 검수 상태.
 * 이미지 판정(확정/확인불가)과 신청 종료(매칭/내역 확인불가)를 구분한다.
 * 내역 상태는 이미지 확정(confirm / confirm-batch) 시점에 확정되고,
 * 실행(run) API 는 상태를 바꾸지 않는다.
 */

export const MAX_MATCH_UNVERIFIABLE = 'UNVERIFIABLE' as const;

/** 검수 완료 = 관리자 확정 또는 확인불가. 미검수가 남으면 실행할 수 없다. */
export const isImageReviewed = (status?: string): boolean =>
  status === 'ADMIN_CONFIRMED' || status === 'UNVERIFIABLE';

export const isUnverifiableStatus = (status?: string): boolean =>
  status === 'UNVERIFIABLE';

/** 처방 이미지 Select 옵션·트리거 — 확인불가는 destructive 로 강조 */
export const imageSelectUnverifiableClassName = (status?: string): string =>
  isUnverifiableStatus(status)
    ? 'text-destructive data-[highlighted]:text-destructive'
    : '';

/** 매칭 완료·내역 확인불가는 추가 검수/실행을 막는다. */
export const isMaxMatchTerminalStatus = (status?: string): boolean =>
  status === 'MATCHED' || status === 'UNVERIFIABLE';

/**
 * Lite 목록에서 미완료(빨간 글씨)가 아닌 상태.
 * ADMIN_CONFIRMED 는 이미지 검수 완료(실행 전)일 수 있다.
 */
export const isMaxMatchMiniSettledStatus = (status?: string): boolean =>
  isMaxMatchTerminalStatus(status) || status === 'ADMIN_CONFIRMED';

/**
 * Lite 생성/검수 잠금.
 * ADMIN_CONFIRMED 만으로는 잠그지 않는다(실행 전이므로).
 * 실행 여부는 maxMatchCreatedAt 으로 본다.
 */
export const isMaxMatchMiniLockedStatus = (status?: string): boolean =>
  isMaxMatchTerminalStatus(status) || status === 'PENDING';

export const hasLiteMaxMatchCreatedAt = (value?: string): boolean => {
  const v = value?.trim();
  return Boolean(v) && v !== '-';
};

/**
 * 관리자 확정(confirm) 가능 여부.
 * 품목이 없어도 confirm 호출 가능 — 서버가 확인불가로 처리한다.
 * 이미 확정·확인불가인 장만 막는다.
 */
export const canAdminConfirmImage = (status?: string): boolean =>
  status !== 'ADMIN_CONFIRMED' && status !== 'UNVERIFIABLE';

export const canMarkImageUnverifiable = (status?: string): boolean =>
  status !== 'ADMIN_CONFIRMED' && status !== 'UNVERIFIABLE';

export const imageReviewBadgeLabel = (status?: string): string => {
  switch (status) {
    case 'ADMIN_CONFIRMED':
      return '확정';
    case 'USER_CONFIRMED':
      return '사용자 확정';
    case 'UNVERIFIABLE':
      return '확인불가';
    case 'NO_ITEMS':
      return '품목 없음';
    default:
      return '미확정';
  }
};

export const maxMatchApplicationStatusLabel = (status: string): string => {
  switch (status) {
    case 'REQUEST':
      return '확인요청';
    case 'ADMIN_CONFIRMED':
      return '관리자 확정';
    case 'USER_CONFIRMED':
      return '사용자 확정';
    case 'PENDING':
      return '진행 대기중';
    case 'MATCHED':
      return '매칭 완료';
    case 'UNVERIFIABLE':
      return '내역 확인불가';
    default:
      return status || '-';
  }
};

/**
 * Lite 이미지 검수값.
 * reviewStatus 가 없으면 isConfirmed 만으로 구분한다.
 * 구 응답의 isConfirmed=true 는 관리자 확정으로 취급한다.
 */
export const resolveMiniImageReviewStatus = (image: {
  reviewStatus?: string;
  isConfirmed?: boolean;
}): string => {
  const reviewStatus = image.reviewStatus?.trim();
  if (reviewStatus) return reviewStatus;
  return image.isConfirmed ? 'ADMIN_CONFIRMED' : 'UNCONFIRMED';
};

/** 확정/확인불가 후 요약·처방 상태 토스트 */
export const reviewFinalizeToast = (applicationStatus?: string): string => {
  if (applicationStatus === 'UNVERIFIABLE') {
    return '내역 확인불가로 처리되었습니다.';
  }
  return '확정이 완료되었습니다.';
};
